import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateFoodSupplementRequest,
  FoodSupplement,
  UpdateFoodSupplementRequest,
} from '@repo/types/src/food-supplement';
import {
  createFoodSupplement,
  deleteFoodSupplement,
  getAllFoodSupplements,
  updateFoodSupplement,
} from './food-supplement.queries';
import { foodSupplementQueryKeys } from './food-supplement.query-keys';
import { queryClient } from '@/config/query-client.config';

export const useGetFoodSupplements = (params?: {
  skip?: number;
  take?: number;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...foodSupplementQueryKeys.all, params],
    queryFn: () => getAllFoodSupplements(params),
  });

  return {
    foodSupplements: response?.data || [],
    count: response?.count || 0,
    isError,
    isLoading,
    refetch,
  };
};

export const useCreateFoodSupplement = () => {
  return useMutation({
    mutationFn: (payload: CreateFoodSupplementRequest) =>
      createFoodSupplement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: foodSupplementQueryKeys.all,
      });
    },
  });
};

export const useUpdateFoodSupplement = () => {
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateFoodSupplementRequest;
    }) => updateFoodSupplement(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: foodSupplementQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: foodSupplementQueryKeys.byId(data.id),
      });
    },
  });
};

export const useDeleteFoodSupplement = () => {
  return useMutation({
    mutationFn: (id: number) => deleteFoodSupplement(id),
    onSuccess: (data) => {
      queryClient.setQueryData<FoodSupplement[]>(
        foodSupplementQueryKeys.all,
        (oldData) => oldData?.filter((item) => item.id !== data.id),
      );
    },
  });
};
